import { useState, useMemo, useCallback, memo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { Eye } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SortableGroup } from './sortable-group'
import type { ColumnItem } from './sortable-item'

interface ColumnVisibilityComboboxProps {
  /** Array of column definitions with visibility state */
  columns: ColumnItem[]
  /** Callback when column visibility changes */
  onVisibilityChange: (columnId: string, isVisible: boolean) => void
  /** Callback for batch visibility changes (Show All / Hide All) */
  onBatchVisibilityChange?: (changes: Record<string, boolean>) => void
  /** Callback when column order changes */
  onOrderChange: (newOrder: string[]) => void
  /** Optional: columns that should not be shown in the visibility menu */
  excludeColumns?: string[]
}

const ColumnVisibilityCombobox = memo(
  ({
    columns,
    onVisibilityChange,
    onBatchVisibilityChange,
    onOrderChange,
    excludeColumns = [],
  }: ColumnVisibilityComboboxProps) => {
    const [open, setOpen] = useState(false)

    // Filter out excluded columns
    const filteredColumns = useMemo(() => {
      return columns.filter((col) => !excludeColumns.includes(col.id))
    }, [columns, excludeColumns])

    // Split into visible and hidden columns
    const { visibleColumns, hiddenColumns, columnMap } = useMemo(() => {
      const visible: ColumnItem[] = []
      const hidden: ColumnItem[] = []
      const map = new Map(filteredColumns.map((col) => [col.id, col]))

      filteredColumns.forEach((col) => {
        if (col.isVisible) {
          visible.push(col)
        } else {
          hidden.push(col)
        }
      })

      return {
        visibleColumns: visible,
        hiddenColumns: hidden,
        columnMap: map,
      }
    }, [filteredColumns])

    // DnD sensors
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    )

    // Toggle single column visibility
    const toggleColumnVisibility = useCallback(
      (columnId: string) => {
        const column = columnMap.get(columnId)
        if (column) {
          onVisibilityChange(columnId, !column.isVisible)
        }
      },
      [columnMap, onVisibilityChange]
    )

    // Hide all visible columns
    const hideAllColumns = useCallback(() => {
      if (onBatchVisibilityChange) {
        const changes: Record<string, boolean> = {}
        visibleColumns.forEach((col) => {
          changes[col.id] = false
        })
        onBatchVisibilityChange(changes)
      } else {
        visibleColumns.forEach((col) => {
          onVisibilityChange(col.id, false)
        })
      }
    }, [visibleColumns, onVisibilityChange, onBatchVisibilityChange])

    // Show all hidden columns
    const showAllColumns = useCallback(() => {
      if (onBatchVisibilityChange) {
        const changes: Record<string, boolean> = {}
        hiddenColumns.forEach((col) => {
          changes[col.id] = true
        })
        onBatchVisibilityChange(changes)
      } else {
        hiddenColumns.forEach((col) => {
          onVisibilityChange(col.id, true)
        })
      }
    }, [hiddenColumns, onVisibilityChange, onBatchVisibilityChange])

    // Handle drag end for reordering
    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event
        if (!over || active.id === over.id) return

        const activeId = active.id as string
        const overId = over.id as string

        // Check if dragging within visible columns
        const isInVisible = visibleColumns.some((col) => col.id === activeId)
        const overIsInVisible = visibleColumns.some((col) => col.id === overId)

        // Check if dragging within hidden columns
        const isInHidden = hiddenColumns.some((col) => col.id === activeId)
        const overIsInHidden = hiddenColumns.some((col) => col.id === overId)

        // Only allow reordering within the same group
        if (isInVisible && overIsInVisible) {
          // Reorder within visible columns
          const visibleOrder = visibleColumns.map((col) => col.id)
          const oldIndex = visibleOrder.indexOf(activeId)
          const newIndex = visibleOrder.indexOf(overId)
          const newVisibleOrder = arrayMove(visibleOrder, oldIndex, newIndex)

          // Build full order: reordered visible columns + hidden columns
          const hiddenOrder = hiddenColumns.map((col) => col.id)
          onOrderChange([...newVisibleOrder, ...hiddenOrder])
        } else if (isInHidden && overIsInHidden) {
          // Reorder within hidden columns
          const hiddenOrder = hiddenColumns.map((col) => col.id)
          const oldIndex = hiddenOrder.indexOf(activeId)
          const newIndex = hiddenOrder.indexOf(overId)
          const newHiddenOrder = arrayMove(hiddenOrder, oldIndex, newIndex)

          // Build full order: visible columns + reordered hidden columns
          const visibleOrder = visibleColumns.map((col) => col.id)
          onOrderChange([...visibleOrder, ...newHiddenOrder])
        }
        // If dragging between groups, do nothing (visibility change handles that)
      },
      [visibleColumns, hiddenColumns, onOrderChange]
    )

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            role="combobox"
            aria-label="Choose visible columns"
            aria-expanded={open}
            className="h-8 w-8 data-[state=open]:border-primary data-[state=open]:bg-primary data-[state=open]:text-white data-[state=open]:[&>svg]:text-white"
          >
            <Eye className="size-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[280px] p-0 rounded-sm"
          align="end"
          sideOffset={8}
        >
          <Command>
            <CommandInput placeholder="Search column" />
            <CommandList className="max-h-[400px]">
              <CommandEmpty>No column found.</CommandEmpty>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                {visibleColumns.length > 0 && (
                  <SortableGroup
                    columns={visibleColumns}
                    toggleAllButtonText="Hide All"
                    title="Shown in table"
                    className="pb-2"
                    isVisible={true}
                    toggleAllColumns={hideAllColumns}
                    toggleColumnVisibility={toggleColumnVisibility}
                  />
                )}
                {hiddenColumns.length > 0 && (
                  <SortableGroup
                    columns={hiddenColumns}
                    toggleAllButtonText="Show All"
                    title="Hidden in table"
                    isVisible={false}
                    toggleAllColumns={showAllColumns}
                    toggleColumnVisibility={toggleColumnVisibility}
                  />
                )}
              </DndContext>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)

ColumnVisibilityCombobox.displayName = 'ColumnVisibilityCombobox'

export { ColumnVisibilityCombobox }
export type { ColumnVisibilityComboboxProps, ColumnItem }
